import * as React from "react";
import { FormEvent, useState } from "react";
import {
  ProcedureEntity,
  ProcedureType,
  ProcedureConsts,
} from "../../../../application/domain/procedures/procedure-entity";
import { UpdateButton } from "./update-button";
import {
  getGroupIdToQueryProc,
  Procedure,
} from "../../../../application/domain/proc-management";
import { useAuthContextV2 } from "../../../../auth-state";
import {
  ProcedureSearchEngineSetting,
  ProcedureTopPageSetting,
} from "@graffer-inc/smart-apply";
import { ProcedurePasscodeSetting } from "../../../molecules/procedure-passcode-setting";
import { ProcedureProcMgtSetting } from "./procedure-proc-mgt-setting";
import { SettingGroupList } from "./setting-group-list";
import { Group } from "../../../../application/domain/group/group";
import { useEffect } from "react";
import {
  ExternalLink,
  RadioButton,
  TextField,
  WarningBanner,
} from "@graffer-inc/console-design-kit";
import { ProcedureCustomPathSetting } from "./custom-path-setting";
import { CustomPathSettings } from "../../../../application/domain/procedures/custom-path";
import { CustomPathValidateResultModal } from "./custom-path-setting/custom-path-validate-result-modal";
import { useCustomPathValidator } from "./custom-path-setting/use-custom-path-validator";
import { CloudGovMarkdownEditor } from "../../common/markdown-editor/markdown-editor";

const DESCRIPTION_VALUE =
  `手続きの説明を入力してください。\n詳しい使い方や申請者が理解しやすくなるポイントについてはヘルプをご確認ください。` as const;

type DetailEditProps = {
  orgSlug: string;
  procedure: ProcedureEntity;
  existingCustomPathSettings: CustomPathSettings;
  isServiceProviderAccount: boolean;
  hasCustomUIDoorPage: boolean;
  // procedure-detail-service.tsxに定義しているonProcedureUpdate()の引数の型は(proc: ProcedureType, customPath?: string)となっており、customPathはオプションとなっている
  // customPathをundefinedにすると、「カスタムパスは更新しない」という意味になり、バックエンドではカスタムパスのバリデーションや保存がスキップされる。
  // しかし、基本情報タブタブにはカスタムパスの入力欄があり、入力欄がからの状態で更新しても、「カスタムパスを空文字で更新する」という意味になる。
  // そのため、基本情報タブからカスタムパスを更新しないというユースケースはない。
  // そのため、このコンポーネントではonProcedureUpdate()の引数の型を(proc: ProcedureType, customPath: string)にし、customPathを必須にする。
  onProcedureUpdate: (proc: ProcedureType, customPath: string) => void;
  useProcMst: boolean;
  procMsts?: Pick<Procedure, "id" | "name">[];
  groups?: Group[];
  onUpdateProcMaster: (procMstId: string) => Promise<void>;
};

export const DetailPage: React.FC<DetailEditProps> = ({
  orgSlug,
  onProcedureUpdate,
  onUpdateProcMaster,
  hasCustomUIDoorPage,
  ...props
}) => {
  const [procedure, setProcedure] = useState<ProcedureEntity>(
    new ProcedureEntity(props.procedure),
  );

  const [newCurrentCustomPath, setNewCurrentCustomPath] = useState<string>(
    props.existingCustomPathSettings?.currentPath ?? "",
  );

  const {
    customPathDuplicateError,
    customPathModalIsActive,
    isValidCurrentCustomPath,
    closeCustomPathModal,
  } = useCustomPathValidator({
    orgId: procedure.orgId,
    procName: procedure.name,
    existingCurrentPath: props.existingCustomPathSettings.currentPath ?? "",
  });

  const onToggleIsSkipArrivalMail = (e: FormEvent<HTMLInputElement>) => {
    procedure.isSkipArrivalMail = e.currentTarget.value === "SKIP";
    setProcedure(procedure.copy());
  };
  const groupIdToProcMst = getGroupIdToQueryProc(useAuthContextV2().auth.data);
  useEffect(() => {
    setProcedure(() => {
      const updateProcedure = new ProcedureEntity(props.procedure);
      updateProcedure.descriptionMarkdown =
        updateProcedure.getInitialDescriptionMarkdown();
      return updateProcedure;
    });
  }, [props.procedure, setProcedure]);

  useEffect(() => {
    setNewCurrentCustomPath(
      props.existingCustomPathSettings?.currentPath ?? "",
    );
  }, [props.existingCustomPathSettings.currentPath, setNewCurrentCustomPath]);

  const handleOnDescriptionMarkdownChange = React.useCallback(
    (value: string) => {
      setProcedure((oldValue) => {
        // 参照：https://github.com/graffer-inc/cloud-gov/pull/18211#discussion_r1818350654
        // summaryとdetailURLをundefinedにしない場合、手続き説明を空で保存すると、summaryとdetailURLが残ってしまう。
        // そのため、手続き説明に触ったタイミングでsummaryとdetailURLをundefinedにする。
        oldValue.summary = undefined;
        oldValue.detailURL = undefined;
        oldValue.descriptionMarkdown = value;
        return oldValue.copy();
      });
    },
    [],
  );

  return (
    <>
      <div className="has-text-right">
        <UpdateButton
          procedure={procedure}
          canModifyAfterPublish={true}
          onClick={async () => {
            if (await isValidCurrentCustomPath(newCurrentCustomPath)) {
              onProcedureUpdate(procedure, newCurrentCustomPath);
            }
          }}
          disabled={
            !procedure.canUpdateBasicInfo(
              props.existingCustomPathSettings.currentPath,
              newCurrentCustomPath,
            )
          }
        />
      </div>
      <div className="field">
        <TextField
          label={"手続き名称"}
          description={"手続き名称を入力してください。"}
          placeholder={""}
          value={procedure.name}
          onChange={(e) => {
            setProcedure((oldValue) => {
              oldValue.name = e.target.value;
              return oldValue.copy();
            });
          }}
          errorMessages={
            !procedure.hasValidName()
              ? "手続き名称は1文字以上、64文字以下の値を入力してください。"
              : undefined
          }
          required
        />
      </div>
      {!!props.groups?.length && <SettingGroupList groups={props.groups} />}
      <div className="field">
        <CloudGovMarkdownEditor
          previewHeaderText="※LGWAN環境ではリンク先は確認できません"
          titleProps={{
            title: "手続き説明",
            description: (
              <div>
                <span
                  style={{
                    whiteSpace: "pre-line",
                  }}
                >
                  {DESCRIPTION_VALUE}
                  <ExternalLink
                    anchor={(props) => (
                      <a
                        {...props}
                        href={"/help/contents/smartapply-summary"}
                        target={"_blank"}
                        rel={"noopener noreferrer"}
                      >
                        {props.children}
                      </a>
                    )}
                  >
                    入力方法やサンプルはこちら
                  </ExternalLink>
                </span>
                {hasCustomUIDoorPage && (
                  <WarningBanner
                    message={
                      "カスタムページが設定されているため、手続き説明は表示されません"
                    }
                    description="既にカスタムページを設定されている場合、手続き説明は表示されません。表示したい場合は自治体デジタルサポートにご依頼ください"
                  />
                )}
              </div>
            ),
          }}
          errorMessages={
            !procedure.hasValidDescriptionMarkdown()
              ? `手続き説明は${ProcedureConsts.MAX_DESCRIPTION_MARKDOWN_LENGTH}文字以内で入力してください。`
              : undefined
          }
          maxLength={ProcedureConsts.MAX_DESCRIPTION_MARKDOWN_LENGTH}
          value={procedure.descriptionMarkdown}
          onChange={handleOnDescriptionMarkdownChange}
        />
      </div>
      <div className="field">
        <ProcedureCustomPathSetting
          orgSlug={orgSlug}
          currentURL={procedure.doorPageURL(
            orgSlug,
            props.existingCustomPathSettings.currentPath,
          )}
          existingCustomPathSettings={props.existingCustomPathSettings}
          newCurrentCustomPath={newCurrentCustomPath}
          setNewCurrentCustomPath={setNewCurrentCustomPath}
        />
      </div>
      <div className="field">
        <RadioButton
          label={"新規申請到達メール通知設定"}
          description={
            "新規の申請が届いたら、手続きを管理するグループの所属ユーザー全員に通知メールを送ります。"
          }
          options={[
            {
              value: "SKIP",
              name: "メールを送信しない",
            },
            {
              value: "SEND",
              name: "メールを送信する",
            },
          ]}
          required={false}
          value={procedure.isSkipArrivalMail ? "SKIP" : "SEND"}
          onChange={onToggleIsSkipArrivalMail}
          direction={"row"}
        />
      </div>
      <ProcedureSearchEngineSetting
        value={procedure.isExcludeFromSearch}
        onChange={() => {
          procedure.isExcludeFromSearch = !procedure.isExcludeFromSearch;
          setProcedure(procedure.copy());
        }}
        disabledBy={
          procedure.isFormAccessRestricted()
            ? "RESTRICTED_FORM_ACCESS"
            : undefined
        }
      />
      <ProcedurePasscodeSetting
        value={procedure.formAccessRestriction?.passcode}
        isValid={procedure.hasValidFormAccessRestriction()}
        onChange={(passcode) => {
          procedure.formAccessRestriction = {
            ...procedure.formAccessRestriction,
            passcode: passcode.length > 0 ? passcode : undefined,
          };
          setProcedure(procedure.copy());
        }}
        disabledBy={
          procedure.isPublished()
            ? "PUBLISHED_PROCEDURE"
            : !procedure.isExcludeFromSearch
              ? "ACTIVE_SEARCH_INDEX"
              : Array.isArray(props.procMsts) && props.procMsts.length > 0
                ? "REFERRED_BY_PROC_MGT"
                : undefined
        }
      />
      <ProcedureTopPageSetting
        value={procedure.isLinkTopPage}
        onChange={(value) => {
          procedure.isLinkTopPage = value;
          setProcedure(procedure.copy());
        }}
      />
      {props.useProcMst && props.procMsts && groupIdToProcMst.canQuery && (
        <ProcedureProcMgtSetting
          procMsts={props.procMsts}
          isServiceProviderAccount={props.isServiceProviderAccount}
          orgId={procedure.orgId}
          groupIds={groupIdToProcMst.groupIds}
          publishStatus={procedure.publishStatus}
          onUpdateProcMaster={onUpdateProcMaster}
          disabledBy={
            procedure.isFormAccessRestricted()
              ? "RESTRICTED_FORM_ACCESS"
              : undefined
          }
        />
      )}
      {!!customPathDuplicateError && (
        <CustomPathValidateResultModal
          customPathDuplicateError={customPathDuplicateError}
          isActive={customPathModalIsActive}
          onClose={closeCustomPathModal}
          onUpdate={() => {
            onProcedureUpdate(procedure, newCurrentCustomPath);
          }}
        />
      )}
    </>
  );
};
